import React, { useEffect } from "react";
import { useLanguage } from "../i18n/LanguageContext";
import { Link } from "../components/L";
import useSEO from "../hooks/useSEO";

const AboutPage = () => {
  const { tx } = useLanguage();
  useSEO({
    title: tx("Rreth Nesh - Akademia e Pare e Alignerave ne Shqiperi"),
    description: tx("Albanian Aligners Academy eshte akademia e pare ne Shqiperi per trajnime profesionale ne fushen e alignerave, ortodoncise dhe dentistrise estetike. Themeluar nga iClear Group."),
    keywords: "rreth nesh, albanian aligners academy, akademia e alignerave, iclear group, trajnime dentare shqiperi",
    canonical: "https://aligners.academy/rreth-nesh",
  });

  return (
    <div data-testid="about-page">
      {/* Hero */}
      <section className="relative py-10 md:py-14 px-6" style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)' }} data-testid="about-hero">
        <div className="max-w-[1350px] mx-auto flex items-center justify-between">
          <div>
            <div className="flex items-center gap-2 text-white/70 text-sm mb-4">
              <Link to="/" className="hover:text-white text-white/70">{tx("Kryefaqe")}</Link>
              <span className="text-white/70">/</span>
              <span className="text-white">{tx("Rreth Nesh")}</span>
            </div>
            <h1 className="text-white" style={{ fontFamily: "'Noto Sans', sans-serif", fontWeight: 700, fontSize: '48px', lineHeight: 1.2 }} data-testid="about-title">
              {tx("Rreth Nesh")}
            </h1>
            <p className="text-white/80 mt-3 max-w-xl">
              {tx("Akademia e pare ne Shqiperi per trajnime profesionale ne fushen e alignerave dhe dentistrise.")}
            </p>
          </div>
        </div>
      </section>

      {/* Kush Jemi */}
      <section className="py-20 md:py-28 px-6 md:px-12 max-w-[1350px] mx-auto" data-testid="kush-jemi">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-2xl font-bold mb-2" style={{ color: 'rgb(28,163,176)', fontFamily: "'Noto Sans', sans-serif" }}>
              {tx("Kush Jemi")}
            </h2>
            <hr className="mb-6" style={{ borderColor: 'rgba(28,163,176,0.1)' }} />
            <p className="text-[#4A5568] mb-4">
              <strong className="text-[#0A0D10]">Albanian Aligners Academy</strong> {tx("eshte themeluar ne vitin 2024 me vizionin e")} <strong className="text-[#0A0D10]">iClear Group</strong>{tx(", si akademia e pare e specializuar ne Shqiperi per trajnime profesionale ne fushen e alignerave dhe dentistrise.")}
            </p>
            <p className="text-[#4A5568] mb-4">
              {tx("E krijuar per te permbushur nevojat ne rritje per edukim cilesor dhe praktik ne ortodoncine moderne, akademia synon te ndertoje ura mes risisive lokale dhe pervojes globale, duke sjelle standarde boterore ne Shqiperi.")}
            </p>
            <p className="text-[#4A5568]">
              {tx("Me mbeshtetjen e ekspertizes se iClear Group dhe partneritetit me doktore, klinika dhe institucione prestigjioze, akademia ofron programe intensive te zhvilluara rikisht ne ambiente trajnuese moderne, te udheheqera nga trajnere me pervoje nderkombetare.")}
            </p>
          </div>
          <div className="relative">
            <div className="overflow-hidden shadow-xl" style={{ borderRadius: '10px' }}>
              <img
                src="/api/files/local/about-auditor.png"
                alt="Albanian Aligners Academy"
                className="w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Pse Aligners Academy */}
      <section className="bg-[#FAFAFA] py-20 md:py-28 px-6 md:px-12" data-testid="pse-ne">
        <div className="max-w-[1350px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="overflow-hidden shadow-xl order-2 lg:order-1" style={{ borderRadius: '10px' }}>
              <img
                src="/api/files/local/about-experts.png"
                alt={tx("Ekspertet nderkombetare")}
                className="w-full h-[400px] object-cover object-top"
              />
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-2xl font-bold mb-2" style={{ color: 'rgb(28,163,176)', fontFamily: "'Noto Sans', sans-serif" }}>
                {tx("Pse Albanian Aligners Academy?")}
              </h2>
              <hr className="mb-6" style={{ borderColor: 'rgba(28,163,176,0.1)' }} />
              <h3 className="text-lg font-bold mb-4 text-[#0A0D10]" style={{ fontFamily: "'Noto Sans', sans-serif" }}>
                {tx("Eksperte Nderkombetare te Ftuar")}
              </h3>
              <p className="text-[#4A5568] mb-6">
                {tx("Ne kuadrin te vizionit per edukim te avancuar, Albanian Aligners Academy bashkepunon me eksperte nderkombetare te ortodoncise dixhitale dhe estetikes dentare. Trajneret tane kane sjelle emra te njohur me pervoje ne mbi 150,000 raste klinike te suksesshme.")}
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  tx("Matthias Peper - Digital Smile Designer"),
                  tx("Dr. Arnaldo Castellucci - Ekspert ne trajtime minimaliste invazive"),
                  tx("Dr. Jomarkys Soares - Specialist ne sistemet aligner"),
                  tx("Dr. Jordi Manauta - Reference globale ne estetiken dentare"),
                  tx("Dr. Chris Chang DDS PHD - Ndikues boteror ne ortodonci"),
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#4A5568]">
                    <i className="fas fa-check-circle mt-1" style={{ color: '#1d88a8' }}></i>
                    {item}
                  </li>
                ))}
              </ul>
              <p className="text-[#4A5568]">
                {tx("Pjesemarrja e tyre e kthen akademine ne nje ure mes risisive lokale dhe pervojes globale, duke sjelle standarde boterore ne Shqiperi.")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Misioni & Vizioni */}
      <section className="py-20 md:py-28 px-6 md:px-12 max-w-[1350px] mx-auto" data-testid="mission-vision">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div style={{ borderRadius: '5px', background: '#fff', padding: '40px 35px', boxShadow: '0 4px 12px 0 rgba(0,0,0,0.06)' }}>
            <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(29,136,168,0.1)' }}>
              <i className="fas fa-bullseye text-xl" style={{ color: '#1d88a8' }}></i>
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: 'rgb(28,163,176)', fontFamily: "'Noto Sans', sans-serif" }}>{tx("Misioni")}</h3>
            <hr className="mb-4" style={{ borderColor: 'rgba(28,163,176,0.1)' }} />
            <p className="text-[#4A5568] mb-4">
              {tx("Ne Albanian Aligners Academy, misioni yne eshte te ndertojme nje platforme edukimi qe frymëzon, formon dhe fuqizon profesionistet dentare qe pasojne risi klinike dhe estetike.")}
            </p>
            <p className="text-[#4A5568] mb-4">
              {tx("Ne besojme se edukimi cilesor nuk duhet te jete shfaqje informimi, por transformimi - nje udhëtim ku cdo pjesemarres zhvillon njohuri, atitudine dhe vetbesimin per te ofruar trajtime moderne me aligners.")}
            </p>
            <p className="text-[#4A5568]">
              {tx("Krijojme hapesira mesimore fizike qe nderthuren praktiken reale me mentorimin e drejtpërdrejte, duke sjelle me te miren nga bota akademike dhe klinike ne cdo kurs.")}
            </p>
          </div>

          <div style={{ borderRadius: '5px', background: '#fff', padding: '40px 35px', boxShadow: '0 4px 12px 0 rgba(0,0,0,0.06)' }}>
            <div className="w-14 h-14 rounded-lg flex items-center justify-center mb-6" style={{ backgroundColor: 'rgba(29,136,168,0.1)' }}>
              <i className="fas fa-eye text-xl" style={{ color: '#1d88a8' }}></i>
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: 'rgb(28,163,176)', fontFamily: "'Noto Sans', sans-serif" }}>{tx("Vizioni")}</h3>
            <hr className="mb-4" style={{ borderColor: 'rgba(28,163,176,0.1)' }} />
            <p className="text-[#4A5568] mb-4">
              {tx("Vizioni i Albanian Aligners Academy eshte te behet qendra udhëheqese rajonale per edukimin profesional ne fushen e ortodoncise moderne, me fokus ne aligners, teknologji dixhitale dhe zhvillim te vazhdueshëm te aftesive klinike.")}
            </p>
            <p className="text-[#4A5568] mb-4">
              {tx("Synojme te ndertojme nje komunitet profesionistesh te motivuar, te certifikuar dhe te pergatitur per te perballuar sfidat e se ardhmes, duke sjelle inovacion dhe cilësi ne kujdesin dentar per cdo pacient.")}
            </p>
            <p className="text-[#4A5568]">
              {tx("Misioni yne eshte te jemi pika e referimit per zhvillimin profesional te vazhdueshëm ne Shqiperi dhe me gjere.")}
            </p>
          </div>
        </div>
      </section>

      {/* Standardi Profesional */}
      <section className="bg-[#FAFAFA] py-20 md:py-28 px-6 md:px-12" data-testid="standardi">
        <div className="max-w-[1350px] mx-auto">
          <h2 className="text-2xl font-bold mb-2" style={{ color: 'rgb(28,163,176)', fontFamily: "'Noto Sans', sans-serif" }}>
            {tx("Standardi Profesional i Trajnimeve")}
          </h2>
          <hr className="mb-10" style={{ borderColor: 'rgba(28,163,176,0.1)' }} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: "fas fa-graduation-cap", title: tx("Certifikim Nderkombetar"), desc: tx("Cdo trajnim perfundon me certifikate te njohur nderkombetarisht, duke validuar njohurite dhe aftesite e fituara gjate programit.") },
              { icon: "fas fa-hands-helping", title: "Praktike Hands-On", desc: tx("Programet tona kombinojne leksione teorike me sesione praktike ne klinike reale, duke siguruar qe cdo pjesemarres te fitoje pervoje te drejtperdrejte.") },
              { icon: "fas fa-microscope", title: tx("Teknologji te Avancuara"), desc: tx("Perdorim pajisje dhe software te fundit ne fushen e ortodoncise dixhitale, duke perfshire skanere 3D, programe planifikimi dhe sisteme aligner te avancuara.") },
              { icon: "fas fa-users", title: tx("Grupe te Vogla"), desc: tx("Per te siguruar cilesine me te larte te mesimit, pranojme nje numer te limituar pjesemarresish ne cdo program, duke mundesuar vemendjen individuale.") },
              { icon: "fas fa-chart-line", title: tx("Zhvillim i Vazhdueshem"), desc: tx("Ofrojme akses te vazhdueshem ne materialet e kursit, update te rregullta dhe mundësi per te ndjekur zhvillimet me te reja ne fushe.") },
              { icon: "fas fa-award", title: tx("Ekspertize Boterore"), desc: tx("Trajneret tane jane referenca boterore ne fushat e tyre, me mijera raste klinike te suksesshme dhe publikime shkencore te shumta.") },
            ].map((item, i) => (
              <div key={i} style={{ borderRadius: '5px', background: '#fff', padding: '30px 25px', boxShadow: '0 4px 12px 0 rgba(0,0,0,0.06)' }}>
                <div className="w-12 h-12 rounded-lg flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(29,136,168,0.1)' }}>
                  <i className={item.icon} style={{ color: '#1d88a8' }}></i>
                </div>
                <h3 className="font-bold text-[#0A0D10] mb-3" style={{ fontFamily: "'Noto Sans', sans-serif" }}>{item.title}</h3>
                <p className="text-[#4A5568] text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bashkepunimet */}
      <section className="py-20 md:py-28 px-6 md:px-12 max-w-[1350px] mx-auto" data-testid="bashkepunimet">
        <h2 className="text-2xl font-bold mb-2" style={{ color: 'rgb(28,163,176)', fontFamily: "'Noto Sans', sans-serif" }}>
          {tx("Bashkepunimet")}
        </h2>
        <hr className="mb-10" style={{ borderColor: 'rgba(28,163,176,0.1)' }} />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[#4A5568] mb-4">
              {tx("Albanian Aligners Academy bashkepunon me institucione te njohura nderkombetare, klinika te specializuara dhe kompani lidere ne industrine dentare per te siguruar cilesine me te larte te edukimit.")}
            </p>
            <p className="text-[#4A5568] mb-4">
              {tx("Partneritetet tona perfshijne bashkepunime me universitete evropiane, qendra kerkimore ne fushen e ortodoncise, si edhe marreveshje me prodhuesit kryesore te sistemeve aligner ne bote.")}
            </p>
            <p className="text-[#4A5568] mb-6">
              {tx("Keto bashkepunime na mundesojne te ofrojme trajnime me materialet dhe teknikat me te reja, duke i dhene pjesemarresve tane nje avantazh konkurrues ne tregun e dentistrise moderne.")}
            </p>
            <div className="flex flex-wrap gap-4">
              {["iClear Group", tx("Institucione Nderkombetare"), tx("Klinika te Specializuara"), tx("Universitete Evropiane")].map((partner, i) => (
                <span key={i} className="px-4 py-2 rounded-full text-sm font-medium border" style={{ borderColor: 'rgba(28,163,176,0.3)', color: '#1d88a8', backgroundColor: 'rgba(28,163,176,0.05)' }}>
                  {partner}
                </span>
              ))}
            </div>
          </div>
          <div className="overflow-hidden shadow-xl" style={{ borderRadius: '10px' }}>
            <img
              src="https://images.pexels.com/photos/23625642/pexels-photo-23625642.jpeg?auto=compress&w=800"
              alt={tx("Bashkepunimet")}
              className="w-full h-[350px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-20 overflow-hidden" data-testid="about-cta">
        <div className="absolute inset-0" style={{ background: 'linear-gradient(130deg, #3CD4E1 0%, #003F72 100%)' }} />
        <div className="relative z-10 max-w-[1350px] mx-auto px-6 text-center text-white">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6" style={{ fontFamily: "'Noto Sans', sans-serif" }}>
            {tx("Bashkohu me ne sot")}
          </h2>
          <p className="text-white/80 mb-10 max-w-xl mx-auto">
            {tx("Zbulo kurset tona, njihu me trajneret dhe fillo udhetimin tend profesional ne boten e alignerave.")}
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/evente"
              className="rounded-full px-8 py-3.5 font-semibold transition-all duration-300 hover:-translate-y-1 inline-flex items-center gap-2 bg-white text-[#003F72] shadow-lg"
              data-testid="about-cta-events"
            >
              {tx("Shiko Eventet")} <i className="fas fa-arrow-right text-sm"></i>
            </Link>
            <Link
              to="/trajnere"
              className="rounded-full px-8 py-3.5 font-semibold transition-all duration-300 hover:-translate-y-1 inline-flex items-center gap-2 border-2 border-white/40 text-white hover:bg-white/10"
              data-testid="about-cta-trainers"
            >
              {tx("Njihu me Trajneret")}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
